import { ADULTS_KEY, PARTICIPATIONS, STAGE_KEYS, type AudienceKey } from '@yoyoclub/shared';

import { HOME_REGIONS, HOME_TIMES, type HomeTime } from '../home/homeState';

/** /picks filters, all kept in the URL (PRD §5.4). Lists mean "any of". */
export interface PicksFilterState {
  audience: AudienceKey | null;
  when: HomeTime;
  regions: string[];
  categories: string[];
  languages: string[];
  participations: string[];
}

export const PICK_LANGUAGES = ['ZH', 'EN', 'BILINGUAL', 'NA'] as const;

export const EMPTY_PICKS_FILTERS: PicksFilterState = {
  audience: null,
  when: 'anytime',
  regions: [],
  categories: [],
  languages: [],
  participations: [],
};

const list = (raw: string | null, allowed?: readonly string[]) =>
  (raw ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter((s, i, all) => !!s && all.indexOf(s) === i && (!allowed || allowed.includes(s)));

export function picksFiltersFromUrl(params: URLSearchParams): PicksFilterState {
  const audienceRaw = params.get('audience') === ADULTS_KEY ? ADULTS_KEY : params.get('stage');
  const audience =
    audienceRaw === ADULTS_KEY || (STAGE_KEYS as readonly string[]).includes(audienceRaw ?? '')
      ? (audienceRaw as AudienceKey)
      : null;
  const whenRaw = params.get('when');
  return {
    audience,
    when: (HOME_TIMES as readonly string[]).includes(whenRaw ?? '')
      ? (whenRaw as HomeTime)
      : 'anytime',
    regions: list(params.get('regions'), HOME_REGIONS),
    // Categories are checked by the API; a stale one simply matches nothing.
    categories: list(params.get('category')),
    languages: list(params.get('lang'), PICK_LANGUAGES),
    participations: list(params.get('participation'), PARTICIPATIONS),
  };
}

/** Writes the filters into the URL, keeping unrelated parameters. */
export function picksFiltersToUrl(f: PicksFilterState, current: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(current);
  for (const k of ['stage', 'audience', 'when', 'regions', 'category', 'lang', 'participation']) {
    next.delete(k);
  }
  if (f.audience === ADULTS_KEY) next.set('audience', ADULTS_KEY);
  else if (f.audience) next.set('stage', f.audience);
  if (f.when !== 'anytime') next.set('when', f.when);
  if (f.regions.length) next.set('regions', f.regions.join(','));
  if (f.categories.length) next.set('category', f.categories.join(','));
  if (f.languages.length) next.set('lang', f.languages.join(','));
  if (f.participations.length) next.set('participation', f.participations.join(','));
  return next;
}

export function picksFilterCount(f: PicksFilterState): number {
  return (
    (f.audience ? 1 : 0) +
    (f.when !== 'anytime' ? 1 : 0) +
    (f.regions.length ? 1 : 0) +
    (f.categories.length ? 1 : 0) +
    (f.languages.length ? 1 : 0) +
    (f.participations.length ? 1 : 0)
  );
}
